import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
} from 'firebase/firestore';
import { Box, Paper, CircularProgress, Typography } from '@mui/material';
import GameList from './GameList';
import ReservationForm from './ReservationForm';
import Countdown from './Countdown';
import { DayData, Game, BookingPeriod, GroupedGames } from '../types/types';
import { useReservations } from '../hooks/useReservations';
import { useServerTime } from '../hooks/useServerTime';
import { useLotteryOperations } from '../hooks/useLotteryOperations';
import { formatDate, formatDateToUI } from '../utils/reservationUtils';

interface FCFSBookingViewProps {
  selectedBookingPeriod: BookingPeriod;
  games: GroupedGames;
  gamesById: { [gameId: number]: Game };
  loadingGames: boolean;
  tokenMap: { [gameId: number]: string };
  saveToken: (gameId: number, token: string, expiresAt: string) => void;
  removeToken: (gameId: number) => void;
  nextTokenExpiration: { gameId?: number; expiresAt?: string };
  removeNextTokenExpiration: () => void;
  isAdmin: boolean;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const FCFSBookingView: React.FC<FCFSBookingViewProps> = ({
  selectedBookingPeriod,
  games,
  gamesById,
  loadingGames,
  tokenMap,
  saveToken,
  removeToken,
  nextTokenExpiration,
  removeNextTokenExpiration,
  isAdmin,
  onSuccess,
  onError,
}) => {
  const [loadingButtons, setLoadingButtons] = useState<{ [gameId: number]: boolean }>({});
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [days, setDays] = useState<{ [date: string]: DayData }>({});
  const [bookingPeriodOpen, setBookingPeriodOpen] = useState<boolean>(false);
  const [disabledGameIds, setDisabledGameIds] = useState<Set<number>>(new Set());

  const { getServerTime } = useServerTime();

  const { disableGame, enableGame } = useLotteryOperations();

  const {
    createInitialReservation,
    confirmReservation,
    cancelInitialReservation,
    cancelConfirmedReservation,
    cancelReservationAsAdmin,
  } = useReservations();

  const startLoading = (gameId: number) => {
    setLoadingButtons((prev) => ({ ...prev, [gameId]: true }));
  };

  const stopLoading = (gameId: number) => {
    setLoadingButtons((prev) => ({ ...prev, [gameId]: false }));
  };

  useEffect(() => {
    const now = new Date(getServerTime());
    setBookingPeriodOpen(
      selectedBookingPeriod.booking_open_at.toDate() <= now &&
      selectedBookingPeriod.booking_close_at.toDate() > now
    );
  }, [selectedBookingPeriod, getServerTime]);

  // Listen to days collection in real-time
  useEffect(() => {
    const currentPeriodRef = doc(db, 'booking_periods', selectedBookingPeriod.id);

    const daysQuery = query(
      collection(db, 'days'),
      where('booking_period_id', '==', currentPeriodRef)
    );

    const unsubscribe = onSnapshot(daysQuery, (querySnapshot) => {
      const daysData: { [date: string]: DayData } = {};

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        if (data.day && data.status) {
          const dayDate = new Date(data.day);
          const formattedDay = formatDate(dayDate);

          daysData[formattedDay] = {
            status: data.status,
            expires_at: data.expires_at || undefined,
            game_id: data.game_id || undefined,
            public_name: data.public_name || undefined,
          };
        }
      });

      setDays(daysData);
    });

    return () => unsubscribe();
  }, [selectedBookingPeriod]);

  // Listen to disabled games in real-time
  useEffect(() => {
    const currentPeriodRef = doc(db, 'booking_periods', selectedBookingPeriod.id);

    const disabledQuery = query(
      collection(db, 'disabled_games'),
      where('booking_period_id', '==', currentPeriodRef)
    );

    const unsubscribe = onSnapshot(disabledQuery, (querySnapshot) => {
      const ids = new Set<number>();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.game_id) {
          ids.add(data.game_id);
        }
      });
      setDisabledGameIds(ids);
    });

    return () => unsubscribe();
  }, [selectedBookingPeriod]);

  useEffect(() => {
    const checkReservationInProgress = () => {
      if (
        nextTokenExpiration.expiresAt &&
        nextTokenExpiration.gameId &&
        new Date(nextTokenExpiration.expiresAt) > new Date()
      ) {
        const game = gamesById[nextTokenExpiration.gameId];
        if (game) {
          setSelectedGame(game);
          setOpenConfirm(true);
        }
      }
    };
    checkReservationInProgress();
  }, [nextTokenExpiration, gamesById]);

  const handleInitialReserve = async (gameId: number, day: string) => {
    startLoading(gameId);

    try {
      const { token, expiresAt } = await createInitialReservation(gameId, day, selectedBookingPeriod);
      saveToken(gameId, token, expiresAt);

      const game = gamesById[gameId];
      if (game) {
        setSelectedGame(game);
        setOpenConfirm(true);
      } else {
        onError('Selected game not found.');
      }
    } catch (err: any) {
      onError(err.message || "Failed to create pending reservation.");
    } finally {
      stopLoading(gameId);
    }
  };

  const handleConfirmReserve = async (
    name: string,
    email: string,
    isPublicName: boolean
  ): Promise<{ success: boolean; message: string } | undefined> => {
    if (!selectedGame) {
      onError('No game selected for reservation.');
      return;
    }

    startLoading(selectedGame.id);

    if (!name) {
      onError('Please enter your name.');
      stopLoading(selectedGame.id);
      return;
    }

    if (!email) {
      onError('Please enter your email.');
      stopLoading(selectedGame.id);
      return;
    }

    const token = tokenMap[selectedGame.id];
    if (!token) {
      onError('No token found for this game.');
      stopLoading(selectedGame.id);
      return;
    }

    try {
      await confirmReservation(
        token,
        selectedGame.id,
        selectedGame.date,
        name,
        email,
        isPublicName,
        selectedGame.homeTeamName,
        selectedGame.awayTeamName,
        selectedBookingPeriod
      );

      onSuccess('Reservation confirmed successfully!');
      setOpenConfirm(false);
      setSelectedGame(null);
      removeNextTokenExpiration();
      return { success: true, message: 'Reservation confirmed successfully!' };
    } catch (err: any) {
      onError(err.message || 'Failed to confirm reservation.');
      return { success: false, message: err.message || 'Failed to confirm reservation.' };
    } finally {
      stopLoading(selectedGame.id);
    }
  };

  const handleCancelInitialReserve = async (gameId: number) => {
    startLoading(gameId);

    const token = tokenMap[gameId];
    if (!token) {
      onError('No reservation token found for the selected game.');
      stopLoading(gameId);
      return;
    }

    try {
      await cancelInitialReservation(token);
      onSuccess('Pending reservation cancelled successfully.');
      stopLoading(gameId);
      removeToken(gameId);
      removeNextTokenExpiration();
      setOpenConfirm(false);
      setSelectedGame(null);
    } catch (err: any) {
      onError(err.message || "Failed to cancel pending reservation.");
      stopLoading(gameId);
    }
  };

  const handleFormCancel = async () => {
    if (selectedGame) {
      await handleCancelInitialReserve(selectedGame.id);
    }
  };

  const handleCancelConfirmedReserve = async (gameId: number) => {
    startLoading(gameId);

    const token = tokenMap[gameId];

    if (isAdmin && !token) {
      try {
        await cancelReservationAsAdmin(gameId, selectedBookingPeriod);
        onSuccess('Reservation cancelled successfully.');
        stopLoading(gameId);
        return;
      } catch (err: any) {
        onError(err.message || "Failed to cancel reservation.");
        stopLoading(gameId);
        return;
      }
    }

    if (!token) {
      onError('No reservation token found for the selected game.');
      stopLoading(gameId);
      return;
    }

    try {
      await cancelConfirmedReservation(token, gameId, selectedBookingPeriod);
      onSuccess('Reservation cancelled successfully.');
      stopLoading(gameId);
      removeToken(gameId);
    } catch (err: any) {
      onError(err.message || "Failed to cancel reservation.");
      stopLoading(gameId);
    }
  };

  const handleCountdownExpire = () => {
    const now = new Date(getServerTime());
    const isOpen = selectedBookingPeriod.booking_open_at.toDate() <= now &&
                   selectedBookingPeriod.booking_close_at.toDate() > now;
    setBookingPeriodOpen(isOpen);
  };

  const handleToggleDisableGame = async (gameId: number, disabled: boolean) => {
    try {
      if (disabled) {
        await disableGame(gameId, selectedBookingPeriod.id);
      } else {
        await enableGame(gameId, selectedBookingPeriod.id);
      }
    } catch (err: any) {
      onError(err.message || 'Pelin tilan muuttaminen epäonnistui.');
    }
  };

  const GameBookingInfo = () => {
    return (
      <Paper
        sx={{
          mt: 0,
          mb: 0,
          p: 3,
          bgcolor: "secondary.main",
          boxShadow: 1,
          width: "100%",
          maxWidth: "md",
          textAlign: "center",
        }}
      >

        <Typography variant="body1" gutterBottom sx={{ color: "text.primary" }}>
          Lippujen varaaminen varausjakson {selectedBookingPeriod.name} otteluihin aukeaa {" "}
          <strong>{formatDateToUI(selectedBookingPeriod.booking_open_at)}</strong>. {" "}
          {selectedBookingPeriod.supplementation_open_at && (
            <>
              Mahdollisesti jäljelle jääviin ottelupäiviin voi ilmoittautua {" "}
              <strong>{formatDateToUI(selectedBookingPeriod.supplementation_open_at)}</strong> - {" "}
              <strong>{formatDateToUI(selectedBookingPeriod.booking_close_at)}</strong>.
            </>
          )}
        </Typography>

        {selectedBookingPeriod.infoText && (
        <Typography variant="body2" sx={{ mt: 2, color: "text.primary" }}>
            {selectedBookingPeriod.infoText}
        </Typography>
        )}

        <Countdown 
          toTime={selectedBookingPeriod.booking_open_at.toDate().toISOString()} 
          onExpire={handleCountdownExpire} 
          bookingEnded={selectedBookingPeriod.booking_close_at.toDate() < new Date(getServerTime())} 
          getServerTime={getServerTime}
        />
      </Paper>
    );
  };

  return (
    <>
      <GameBookingInfo />
      <Box sx={{ width: "100%", maxWidth: "md", mt: 4 }}>
        <Paper elevation={3} sx={{ padding: 2, bgcolor: 'background.paper', color: 'primary.main' }}>
          {loadingGames ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <GameList
              games={games}
              days={days}
              loadingButtons={loadingButtons}
              onReserve={handleInitialReserve}
              onCancelInitial={handleCancelInitialReserve}
              onCancelConfirmed={handleCancelConfirmedReserve}
              onToggleDisableGame={handleToggleDisableGame}
              bookingPeriodOpen={bookingPeriodOpen}
              tokenMap={tokenMap}
              isAdmin={isAdmin}
              mode="FCFS"
              disabledGameIds={disabledGameIds}
            />
          )}
        </Paper>
      </Box>

      {selectedGame && (
        <ReservationForm
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={handleConfirmReserve}
          onCancel={handleFormCancel}
          expiresAt={selectedGame ? days[selectedGame.date]?.expires_at : undefined}
          selectedGame={selectedGame}
          mode="FCFS"
        />
      )}
    </>
  );
};

export default FCFSBookingView;
